/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1712275200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Apr 05 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amazon-kinesis",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to your AWS account",
          "depth": 1,
          "id": "connect-to-your-aws-account"
        }, {
          "value": "Select your AWS region",
          "depth": 1,
          "id": "select-your-aws-region"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Configure your events trigger",
          "depth": 1,
          "id": "configure-your-events-trigger"
        }, {
          "value": "Choose your data stream",
          "depth": 1,
          "id": "choose-your-data-stream"
        }, {
          "value": "Customize your streaming data",
          "depth": 1,
          "id": "customize-your-streaming-data"
        }, {
          "value": "Configure optional message properties",
          "depth": 1,
          "id": "configure-optional-message-properties"
        }, {
          "value": "Configure initial sync behavior",
          "depth": 1,
          "id": "configure-initial-sync-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      strong: "strong",
      img: "img",
      code: "code",
      ul: "ul",
      li: "li",
      h4: "h4",
      ol: "ol",
      pre: "pre",
      span: "span",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h5: "h5"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch integrates directly with Amazon Kinesis to support high-throughput, distributed, or asynchronous workloads, letting you build a custom connector to your internal systems."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-aws-account",
        children: "Connect to your AWS account"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up the Amazon Kinesis destination for the first time, you need to enter your ", _jsx(_components.strong, {
          children: "AWS Credentials"
        }), " to give Hightouch access to your AWS account. Hightouch needs permission to send streaming data to Amazon Kinesis on your behalf."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-amazon-kinesis-set-up.png",
          alt: "Entering AWS Credentials in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch believes in the principle of least privilege. We ask for no more permissions than necessary. For the Amazon Kinesis destination, Hightouch requires ", _jsx(_components.code, {
          children: "kinesis:PutRecords"
        }), " and ", _jsx(_components.code, {
          children: "kinesis:ListStreams"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When configuring your credentials in Hightouch, you have two options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Setting up a cross-account role (recommended)"
        }), "\n", _jsx(_components.li, {
          children: "Providing an access key"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "set-up-cross-account-role",
        children: "Set up cross-account role"
      }), "\n", _jsx(_components.p, {
        children: "Cross-account roles are the most secure method for granting Hightouch access to Amazon Kinesis in your AWS account."
      }), "\n", _jsxs(_components.p, {
        children: ["You need the ", _jsx(_components.strong, {
          children: "Account ID"
        }), " and ", _jsx(_components.strong, {
          children: "External ID"
        }), " in the Hightouch UI to set up a cross-account role."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-aws-cross-account-role.png",
          alt: "Account ID and External ID in Hightouch"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "AWS Console"
          }), ", navigate to ", _jsx(_components.strong, {
            children: "IAM"
          }), " → ", _jsx(_components.strong, {
            children: "Roles"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Create Role"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Trusted entity type"
          }), ", choose ", _jsx(_components.strong, {
            children: "AWS account"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Another AWS account"
          }), " and also click ", _jsx(_components.strong, {
            children: "Require external ID"
          }), ".\n", _jsx(_components.img, {
            src: "destinations/destination-aws-iam-create-role.png",
            alt: "AWS account configuration"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy/paste the ", _jsx(_components.strong, {
            children: "Account ID"
          }), " from Hightouch into the ", _jsx(_components.strong, {
            children: "Account ID"
          }), " field in AWS."]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy/paste the ", _jsx(_components.strong, {
            children: "External ID"
          }), " from Hightouch into the ", _jsx(_components.strong, {
            children: "External ID"
          }), " field in AWS."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Add permissions"
          }), " screen, proceed to attach permissions to the role using any policy that includes at least ", _jsx(_components.code, {
            children: "kinesis:PutRecords"
          }), " and ", _jsx(_components.code, {
            children: "kinesis:ListStreams"
          }), ". Then create the role.\n", _jsx(_components.img, {
            src: "destinations/destination-amazon-sqs-copy-role-arn.png",
            alt: "AWS ARN"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy/paste the ", _jsx(_components.strong, {
            children: "Role ARN"
          }), " from AWS into Hightouch. Click ", _jsx(_components.strong, {
            children: "Create"
          }), " to finish setting up your cross-account role.\n", _jsx(_components.img, {
            src: "destinations/destination-aws-cross-account-role-arn.png",
            alt: "Pasting the AWS ARN in Hightouch"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "provide-access-key",
        children: "Provide access key"
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't want to create a cross-account role, you can create a regular IAM user and share a ", _jsx(_components.strong, {
          children: "Access Key ID"
        }), " and ", _jsx(_components.strong, {
          children: "Secret Access Key"
        }), " with Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-aws-access-key.png",
          alt: "Entering Access Key ID and Secret Access Key into Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Be sure to attach the user to a permission policy that includes ", _jsx(_components.code, {
          children: "kinesis:PutRecords"
        }), " and ", _jsx(_components.code, {
          children: "kinesis:ListStreams"
        }), " for the specific resource you want to use via a Hightouch sync."]
      }), "\n", _jsx(_components.p, {
        children: "An example policy looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:PutRecords\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:ListStreams\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_KINESIS_ARN_1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_KINESIS_ARN_2\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_KINESIS_ARN_3\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "select-your-aws-region",
        children: "Select your AWS region"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you specify which region has the resources you want to sync to."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Once you've authenticated your AWS account in Hightouch and selected an AWS Region, you've completed setup for a Amazon Kinesis destination in your Hightouch workspace. The next step is to configure a sync that send messages whenever rows are added, changed, or removed in your model."
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-events-trigger",
        children: "Configure your events trigger"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch monitors your data model for added, changed, and removed rows. In this step, you specify which of these events should trigger message publishing."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-events-trigger.png",
          alt: "Events to trigger message publishing"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-data-stream",
        children: "Choose your data stream"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you choose which data stream to send messages to. Hightouch allows you to sync to existing data streams that are already in your Amazon Kinesis."
      }), "\n", _jsx(_components.h3, {
        id: "customize-your-streaming-data",
        children: "Customize your streaming data"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-customize-message-data.png",
          alt: "JSON streaming data options in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to build the JSON streaming data object using data from your model."
      }), "\n", _jsx(_components.h4, {
        id: "configure-partition-key",
        children: "Configure partition key"
      }), "\n", _jsxs(_components.p, {
        children: ["This field is sent along with the streaming data and determines which shard in the stream the data record is assigned to. Amazon Kinesis Data Streams uses the partition key as input to a hash function that maps the partition key and associated data to a specific shard. Specifically, an MD5 hash function is used to map partition keys to 128-bit integer values and to map associated data records to shards. As a result of this hashing mechanism, all data records with the same partition key map to the same shard within the stream.\n", _jsx(_components.img, {
          src: "destinations/destination-amazon-kinesis-choose-partition-key.png",
          alt: "Configure partition key"
        })]
      }), "\n", _jsx(_components.p, {
        children: "This destination offers three methods of composing a JSON object:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-json-editor",
            children: "Using a JSON editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-one-column-from-model",
            children: "Using one column from a model"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-multiple-columns-from-model",
            children: "Using multiple columns from a model"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-json-editor",
        children: "Use JSON editor"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-json-editor.png",
          alt: "Selecting the JSON editor method in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["With the JSON editor, you can compose any JSON object using the ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid template language"
        }), ". This is particularly useful for complex streaming data bodies containing nested objects and arrays, which can sometimes be difficult to model entirely in SQL."]
      }), "\n", _jsx(_components.p, {
        children: "Suppose your data model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "full_name"
            }), _jsx(_components.th, {
              children: "age"
            }), _jsx(_components.th, {
              children: "email_address"
            }), _jsx(_components.th, {
              children: "phone_number"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "John Doe"
            }), _jsx(_components.td, {
              children: "30"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "john@example.com"
              })
            }), _jsx(_components.td, {
              children: "+14158675309"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "And you want your streaming data like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"age\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "30"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"contact_info\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"john@example.com\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"phone\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"+14158675309\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Your Liquid template should look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-txt",
          children: "{\n  \"name\": \"{{row.full_name}}\",\n  \"age\": {{row.age}},\n  \"contact_info\": [\n    {\n      \"type\": \"email\",\n      \"value\": \"{{row.email_address}}\"\n    },\n    {\n      \"type\": \"phone\",\n      \"value\": \"{{row.phone_number}}\"\n    }\n  ]\n}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This makes it so you can reference any column using the syntax ", _jsx(_components.code, {
          children: "{{row.column_name}}"
        }), ". You can also use advanced Liquid features to incorporate control flow and loops into your dynamic streaming data."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When injecting strings into your JSON body, be sure to surround the Liquid tag\nin double quotes."
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-one-column-from-model",
        children: "Use one column from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-one-column-from-model.png",
          alt: "Selecting using one column from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you're already storing JSON data in your source, or if you have the ability to construct a JSON object using SQL, you can select one column in your model that already contains the full streaming data."
      }), "\n", _jsx(_components.p, {
        children: "This setting is commonly used when syncing web events that have already been collected and stored as JSON objects in your database."
      }), "\n", _jsx(_components.h4, {
        id: "use-multiple-columns-from-model",
        children: "Use multiple columns from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-multiple-columns-from-model.png",
          alt: "Selecting using multiple columns from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "For the simplest use cases, Hightouch can construct a JSON object with key/value pairs based on multiple columns in your model."
      }), "\n", _jsx(_components.p, {
        children: "Suppose your model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "email"
            }), _jsx(_components.th, {
              children: "first_name"
            }), _jsx(_components.th, {
              children: "last_name"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alice.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bob.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "carol.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Carol"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The field mapping in the screenshot above would generate the following streaming data for the first row:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Alice\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alice.doe@example.com\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can use the field mapper to rename fields. For example, ", _jsx(_components.code, {
            children: "first_name"
          }), " can\nbe mapped to ", _jsx(_components.code, {
            children: "customer_first_name"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-optional-message-properties",
        children: "Configure optional message properties"
      }), "\n", _jsx(_components.p, {
        children: "Along with your row data in JSON format, you can also optionally include an explicit hash key to explicitly determine the shard the data record is assigned to."
      }), "\n", _jsx(_components.h5, {
        id: "explicithashkey",
        children: _jsx(_components.code, {
          children: "ExplicitHashKey"
        })
      }), "\n", _jsx(_components.p, {
        children: "This is a string field that should be a hash value and will override the partition key hash."
      }), "\n", _jsx(_components.h3, {
        id: "configure-initial-sync-behavior",
        children: "Configure initial sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to handle rows present in your model results during the first sync run."
      }), "\n", _jsx(_components.p, {
        children: "Certain workflows may require performing a backfill of all rows during the initial sync. For other use cases, you might only want to send messages in response to future data changes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-initial-sync-behaviour.png",
          alt: "Declaring how to handle initial sync behavior in Hightouch"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "provisionedthroughputexceededexception-rate-exceeded-for-shard",
        children: "ProvisionedThroughputExceededException: Rate exceeded for shard."
      }), "\n", _jsxs(_components.p, {
        children: ["This error refers to Amazon Kinesis’ ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/streams/latest/dev/service-sizes-and-limits.html",
          children: "quotas and limits"
        }), ". Manual intervention isn't required since our sync engine ", _jsx(_components.a, {
          href: "/syncs/retries",
          children: "retries rejected rows"
        }), " during the following sync run."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
